import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { OperatorFunction, filter, startWith } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Tariff } from '../tariff.model';
import { SelectTariffProcessFields } from '../../kkms/sign-selected-tariff-page/sign-selected-tariff-page.model';
import { RegisterKkmFields } from '../../kkms/new-kkm/register-kkm.model';
import { TariffFormModel } from './select-tariff-connection.types';

@UntilDestroy()
@Component({
  selector: 'tax-select-tariff-connection',
  templateUrl: 'select-tariff-connection.component.html',
  styleUrls: ['select-tariff-connection.component.sass'],
})

export class SelectTariffConnectionComponent implements OnInit {
  @Input() set kkmProcessFields(kkmProcessFields: SelectTariffProcessFields | RegisterKkmFields | undefined) {
    if (this.isBpChangeTariff(kkmProcessFields) && kkmProcessFields.promoCodeIsAllowed === 'false') {
      this.tariffForm.controls.promoCode.disable();
    } else {
      this.tariffForm.patchValue({
        promoCode: kkmProcessFields?.billingPromoCode || '',
      });
    }

    this.tariffForm.patchValue({
      consultantPhone: kkmProcessFields?.consultantPhone,
      simNumber: kkmProcessFields?.kkmConnectionId || undefined,
    });
  }

  @Input() set tariff(tariff: Tariff | undefined) {
    this._tariff = tariff;
    this.tariffForm.patchValue({
      connectionType: tariff?.connectionType?.connection,
    });
  }

  @Output() formChanges = new EventEmitter<FormGroup<TariffFormModel>>();

  _tariff?: Tariff;

  readonly tariffForm = this.createTariffForm();

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.updateSimNumberOnConnectionTypeChange();
    this.sendFormToParentOnFormChange();
    this.convertPromoCode2Uppercase();
  }

  private convertPromoCode2Uppercase(): void {
    const promoCodeInput = this.tariffForm.controls.promoCode;

    promoCodeInput.valueChanges
      .pipe(
        filter(promoCode => Boolean(promoCode)) as OperatorFunction<string | null, string>,
        untilDestroyed(this),
      )
      .subscribe((promoCode) => {
        promoCodeInput.setValue(promoCode?.toUpperCase(), {
          emitEvent: false,
        });
      });
  }

  /**
   * Компонент может использоваться в БП создания ККМ и в БП добавления/изменения тарифа
   */
  private isBpChangeTariff(kkmProcessFields: SelectTariffProcessFields | RegisterKkmFields | undefined): kkmProcessFields is SelectTariffProcessFields {
    if (!kkmProcessFields) return false;

    return (kkmProcessFields as SelectTariffProcessFields).promoCodeIsAllowed !== undefined;
  }

  private sendFormToParentOnFormChange(): void {
    this.tariffForm.valueChanges
      .pipe(
        startWith(this.tariffForm.value),
        untilDestroyed(this),
      )
      .subscribe(() => {
        if (this.tariffForm.value.promoCode === null) {
          this.tariffForm.patchValue({
            promoCode: '',
          });
        }
        this.formChanges.emit(this.tariffForm);
      });
  }

  private updateSimNumberOnConnectionTypeChange(): void {
    this.tariffForm.controls.connectionType.valueChanges
      .pipe(
        startWith(this.tariffForm.controls.connectionType.value),
        untilDestroyed(this),
      )
      .subscribe((connectionType) => {
        if (connectionType === 'WIRELESS') {
          this.tariffForm.controls.simNumber.enable();
          this.tariffForm.get('simNumber')?.addValidators(Validators.required);
        } else {
          this.tariffForm.get('simNumber')?.reset();
          this.tariffForm.controls.simNumber.disable();
          this.tariffForm.get('simNumber')?.removeValidators(Validators.required);
        }
        this.tariffForm.get('simNumber')?.updateValueAndValidity({
          onlySelf: true, emitEvent: false,
        });
      });
  }

  private createTariffForm(): FormGroup<TariffFormModel> {
    return this.fb.group<TariffFormModel>({
      connectionType: new FormControl(null, Validators.required),
      consultantPhone: new FormControl(null),
      simNumber: new FormControl(
        {
          value: '', disabled: true,
        },
        {
          nonNullable: true,
        },
      ),
      promoCode: new FormControl('', {
        nonNullable: true,
      }),
    });
  }
}
